import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Grid,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  Button,
  CircularProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import FromHomeTab from "../OfflinePaymentScreen/FromHomeTab";
import FromStoreTab from "../OfflinePaymentScreen/FromStoreTab";
import { getAppConfigRequest, getStoreListForUserRequest } from "../../redux/Dashboard/dashboardSlice";
import { resetOfflineOrderState } from "../../redux/OfflineOrder/offlineOrderSlice";
import {
  createSipOfflineOrderRequest,
  resetCreatedSipOrder,
  resetSipOfflineOrder,
} from "../../redux/Sip/sipSlice";
import { useAppTheme } from "../../theme/useAppTheme";
import useCurrency from "../../hooks/useCurrency";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";

const OfflineSIPPaymentScreen = () => {
  const { state } = useLocation();
  const dispatch = useDispatch();
  const sipOfflineStarted = useRef(false);

  const { storeList, appConfig } = useSelector((state) => state.dashboard || {});
  const sipOfflineLoading = useSelector((state) => state.sip?.sipOfflineLoading);
  const sipOfflineOrder = useSelector((state) => state.sip?.sipOfflineOrder);
  const sipOfflineError = useSelector((state) => state.sip?.sipOfflineError);

  const offlineSIPOrderData = state?.offlineSIPOrderData;
  const orderId = sipOfflineOrder?.orderId || "";

  const offlinePaymentDetails = appConfig?.offlinePaymentDetails;
  const decimalPrecision = appConfig?.decimalPrecision || 2;
  const offlinePaymentType = appConfig?.offlinePaymentType;
  const configReady = Boolean(appConfig);
  const showHome = configReady
    ? offlinePaymentType
      ? Boolean(offlinePaymentType.home)
      : true
    : false;
  const showStore = configReady
    ? offlinePaymentType
      ? Boolean(offlinePaymentType.store)
      : true
    : false;

  const themeColors = useAppTheme();
  const { symbol } = useCurrency();

  const [tab, setTab] = useState(null);
  const [openQr, setOpenQr] = useState(false);

  useEffect(() => {
    if (!configReady) return;
    setTab((current) => {
      if (current === "home" && showHome) return current;
      if (current === "store" && showStore) return current;
      if (showHome) return "home";
      if (showStore) return "store";
      return null;
    });
  }, [configReady, showHome, showStore]);

  const buildSipOfflinePayload = useCallback(
    () => ({
      weight: Number(offlineSIPOrderData?.weight) || 0,
      metalName: offlineSIPOrderData?.metalName || "",
      orderType: "Installment",
      final_amount: Number(offlineSIPOrderData?.final_amount) || 0,
      tax: Number(offlineSIPOrderData?.tax) || 0,
      noOfMonths: Number(offlineSIPOrderData?.noOfMonths) || 0,
      Id: offlineSIPOrderData?.Id || "",
      sipDay: Number(offlineSIPOrderData?.sipDay) || 0,
      purity: Number(offlineSIPOrderData?.purity) || 0,
      planid: offlineSIPOrderData?.planid || "",
      installment_amount: Number(offlineSIPOrderData?.installment_amount) || 0,
      x_api_version: "2022-09-01",
    }),
    [offlineSIPOrderData],
  );

  useEffect(() => {
    dispatch(resetSipOfflineOrder());
    dispatch(resetCreatedSipOrder());
    dispatch(resetOfflineOrderState());
    dispatch(getAppConfigRequest());
    dispatch(
      getStoreListForUserRequest({
        id: `${process.env.REACT_APP_JEWELLER_ID}`,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (!configReady || !showHome || tab !== "home") return;
    if (sipOfflineStarted.current || !offlineSIPOrderData) return;
    if (!offlineSIPOrderData.planid) {
      sipOfflineStarted.current = true;
      toast.error("SIP plan is missing");
      return;
    }
    sipOfflineStarted.current = true;
    dispatch(createSipOfflineOrderRequest(buildSipOfflinePayload()));
  }, [
    configReady,
    showHome,
    tab,
    dispatch,
    offlineSIPOrderData,
    buildSipOfflinePayload,
  ]);

  const handleRetry = () => {
    if (!offlineSIPOrderData?.planid) {
      toast.error("SIP plan is missing");
      return;
    }
    dispatch(createSipOfflineOrderRequest(buildSipOfflinePayload()));
  };

  const handleCopy = async (value) => {
    try {
      await navigator.clipboard.writeText(String(value));
      toast.success("Copied!");
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  if (!offlineSIPOrderData) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          Offline SIP order data not found!
        </Typography>
      </Box>
    );
  }

  const metalLabel = [
    offlineSIPOrderData.metalName,
    offlineSIPOrderData.karat ? `${offlineSIPOrderData.karat}k` : null,
    offlineSIPOrderData.purity ? offlineSIPOrderData.purity : null,
  ]
    .filter((part) => part !== null && part !== undefined && part !== "")
    .join(" - ");

  const storeOrderData = {
    ...offlineSIPOrderData,
    orderId: orderId || offlineSIPOrderData.orderId || "",
  };

  return (
    <Box
      sx={{
        background: themeColors.scaffoldGradientColors,
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        p: 4,
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 900,
          borderRadius: 3,
          boxShadow: 5,
          p: 3,
          background: themeColors.bigContainer,
          border: `1px solid ${themeColors.scaffold}`,
          color: themeColors.text,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: themeColors.primary,
            fontFamily: "Aeonik",
            mb: 2,
            textAlign: "center",
          }}
        >
          Offline Payment
        </Typography>

        <Card
          sx={{
            borderRadius: 3,
            mb: 3,
            boxShadow: 3,
            background: themeColors.bigContainer,
            border: `1px solid ${themeColors.scaffold}`,
            color: themeColors.text,
          }}
        >
          <CardContent>
            <Typography
              variant="h6"
              sx={{ mb: 2, fontFamily: "Aeonik", fontWeight: 600 }}
            >
              Order Details
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1,
                mb: 2,
                borderRadius: 3,
                background: themeColors.mediumContainer,
                border: `1px solid ${themeColors.scaffold}`,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  width: { xs: 90, sm: 110 },
                  flexShrink: 0,
                }}
              >
                Order ID :
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  flex: 1,
                  minWidth: 0,
                  wordBreak: "break-all",
                }}
              >
                {sipOfflineLoading
                  ? "Creating order..."
                  : orderId || "—"}
              </Typography>
              {orderId ? (
                <Tooltip title="Copy">
                  <IconButton
                    size="small"
                    onClick={() => handleCopy(orderId)}
                    sx={{ color: themeColors.text }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Box>

            <Grid container spacing={2}>
              {[
                ["Metal", metalLabel],
                ["Weight", `${offlineSIPOrderData.weight} gms`],
                ["Months", `${offlineSIPOrderData.noOfMonths}`],
              ].map(([label, value]) => (
                <Grid size={{ xs: 12, sm: 4 }} key={label}>
                  <Box
                    sx={{
                      height: "100%",
                      px: 2,
                      py: 1.5,
                      borderRadius: 3,
                      textAlign: "center",
                      background: themeColors.mediumContainer,
                      border: `1px solid ${themeColors.scaffold}`,
                    }}
                  >
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      {label}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {value}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 2, opacity: 0.3 }} />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1,
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Amount
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                {symbol}
                {Number(offlineSIPOrderData.final_amount || 0).toFixed(
                  decimalPrecision,
                )}
              </Typography>
            </Box>

            {sipOfflineError && !orderId && (
              <Box sx={{ mt: 2, textAlign: "center" }}>
                <Typography color="error" sx={{ mb: 1 }}>
                  {sipOfflineError}
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleRetry}
                  sx={{
                    background: themeColors.primary,
                    color: themeColors.text,
                    textTransform: "none",
                  }}
                >
                  Retry
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {tab === "home" && showHome && offlinePaymentDetails && (
          <Card
            sx={{
              borderRadius: 3,
              mb: 3,
              boxShadow: 3,
              background: themeColors.bigContainer,
              border: `1px solid ${themeColors.scaffold}`,
              color: themeColors.text,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{ mb: 2, fontFamily: "Aeonik", fontWeight: 600 }}
              >
                Payment Details
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                <Grid
                  size={{
                    xs: 12,
                    md: offlinePaymentDetails.qrCodeUrl ? 8 : 12,
                  }}
                >
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                  >
                    {[
                      ["Bank Name", offlinePaymentDetails.bankName],
                      ["Account No", offlinePaymentDetails.accountNo],
                      ["IFSC Code", offlinePaymentDetails.ifscCode],
                      ["UPI ID", offlinePaymentDetails.upiId],
                    ]
                      .filter(([, value]) => value)
                      .map(([label, value]) => (
                        <Box
                          key={label}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 2,
                            py: 1,
                            borderRadius: 3,
                            background: themeColors.mediumContainer,
                            border: `1px solid ${themeColors.scaffold}`,
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              width: { xs: 90, sm: 110 },
                              flexShrink: 0,
                            }}
                          >
                            {label} :
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 700,
                              flex: 1,
                              minWidth: 0,
                              wordBreak: "break-all",
                            }}
                          >
                            {value}
                          </Typography>
                          <Tooltip title="Copy">
                            <IconButton
                              size="small"
                              onClick={() => handleCopy(value)}
                              sx={{ color: themeColors.text }}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ))}
                  </Box>
                </Grid>

                {offlinePaymentDetails.qrCodeUrl && (
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Box
                      onClick={() => setOpenQr(true)}
                      sx={{
                        height: "100%",
                        minHeight: 160,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        p: 2,
                        borderRadius: 3,
                        cursor: "pointer",
                        textAlign: "center",
                        border: `2px dashed ${themeColors.scaffold}`,
                        background: themeColors.mediumContainer,
                        transition: "transform 0.2s",
                        "&:hover": { transform: "translateY(-3px)" },
                      }}
                    >
                      <QrCode2Icon
                        sx={{ fontSize: 80, color: themeColors.text }}
                      />
                      <Typography sx={{ fontWeight: 700 }}>
                        Scan to Pay
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        sx={{
                          borderRadius: "20px",
                          textTransform: "none",
                          background: themeColors.primary,
                          color: themeColors.text,
                          border: `1px solid ${themeColors.scaffold}`,
                          "&:hover": { background: themeColors.primary },
                        }}
                      >
                        View QR
                      </Button>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}

        {(showHome || showStore) && tab && (
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            textColor="primary"
            indicatorColor="primary"
            variant="fullWidth"
            sx={{ mb: 2 }}
          >
            {showHome && (
              <Tab
                value="home"
                label="From Home"
                sx={{ fontWeight: tab === "home" ? 600 : 500 }}
              />
            )}
            {showStore && (
              <Tab
                value="store"
                label="From Store"
                sx={{ fontWeight: tab === "store" ? 600 : 500 }}
              />
            )}
          </Tabs>
        )}

        {(showHome || showStore) && <Divider sx={{ mb: 2 }} />}

        <Card
          sx={{
            borderRadius: 3,
            p: 2,
            boxShadow: 3,
            background: themeColors.bigContainer,
            border: `1px solid ${themeColors.scaffold}`,
            color: themeColors.text,
          }}
        >
          {tab === "home" && showHome ? (
            sipOfflineLoading && !orderId ? (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <CircularProgress size={28} />
                <Typography sx={{ mt: 1 }}>Creating offline SIP order</Typography>
              </Box>
            ) : orderId ? (
              <FromHomeTab orderID={orderId} />
            ) : (
              <Typography sx={{ textAlign: "center", py: 2 }}>
                Offline order is not created yet. Use Retry in Order Details.
              </Typography>
            )
          ) : tab === "store" && showStore ? (
            <FromStoreTab
              storeList={storeList}
              offlineOrderData={storeOrderData}
              orderType="SIP"
            />
          ) : (
            <Typography sx={{ textAlign: "center", py: 2 }}>
              {configReady
                ? "Offline payment is not available."
                : "Loading payment options..."}
            </Typography>
          )}
        </Card>
      </Card>

      <Dialog
        open={openQr}
        onClose={() => setOpenQr(false)}
        maxWidth="xs"
        fullWidth
      >
        <IconButton
          onClick={() => setOpenQr(false)}
          sx={{ position: "absolute", top: 8, right: 8 }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ textAlign: "center", pt: 6 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Scan to Pay
          </Typography>
          <Box
            component="img"
            src={offlinePaymentDetails?.qrCodeUrl}
            alt="Payment QR Code"
            sx={{ width: "100%", maxWidth: 300, borderRadius: 2 }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default OfflineSIPPaymentScreen;
